import {
  alpha,
  Avatar,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputBase,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import SearchIcon from '@mui/icons-material/Search'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useBasePath } from 'components/Providers'
import { stringToColor } from 'components/Avatar'
import dayjs from 'dayjs'

const initials = person =>
  person
    ? `${person.firstName?.[0] || ''}${person.lastName?.[0] || ''}`.toUpperCase()
    : '?'

export default function MenteesList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const list = useSelector(state => state.mentorship.mentorship.list)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('active')

  const filtered = list?.filter(m => {
    const isActive = !m.endDate
    if (status === 'active' && !isActive) return false
    if (status === 'inactive' && isActive) return false
    if (!search) return true
    const name = `${m.mentee?.firstName} ${m.mentee?.lastName}`.toLowerCase()
    return name.includes(search.toLowerCase())
  })

  if (list === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ my: 3 }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            overflow: 'hidden',
            height: 50,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1.5, flex: 1 }}>
            <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <InputBase
              placeholder={t('Search mentees...')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ fontSize: 13, flex: 1 }}
            />
          </Stack>
          <Divider orientation="vertical" flexItem />
          {[
            { value: 'active', label: t('Active') },
            { value: 'inactive', label: t('Inactive') },
            { value: 'all', label: t('All') },
          ].map(({ value, label }) => (
            <ButtonBase
              key={value}
              onClick={() => setStatus(value)}
              sx={{
                px: 2,
                height: '100%',
                fontSize: 13,
                fontWeight: status === value ? 600 : 400,
                color: status === value ? 'primary.main' : 'text.secondary',
                bgcolor: status === value ? theme => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                borderRight: '1px solid',
                borderColor: 'divider',
                transition: 'background 0.15s, color 0.15s',
                '&:last-child': { borderRight: 'none' },
              }}
            >
              {label}
            </ButtonBase>
          ))}
        </Paper>
        <Typography variant="body2" color="text.secondary">
          {filtered?.length ?? 0} {t('mentees')}
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        {(filtered || []).map(m => {
          const menteeName = m.mentee
            ? `${m.mentee.lastName}, ${m.mentee.firstName}`
            : '—'
          const mentorName = m.mentor
            ? `${m.mentor.lastName}, ${m.mentor.firstName}`
            : '—'
          const isActive = !m.endDate
          const color = stringToColor(menteeName)
          const approvedCount = Number(m.approvedCount) || 0
          const pendingCount = Number(m.pendingCount) || 0
          const rejectedCount = Number(m.rejectedCount) || 0
          const totalCount = Number(m.totalCount) || 0

          return (
            <Grid item xs={12} sm={6} md={4} key={m.id}>
              <Paper
                elevation={0}
                onClick={() => navigate(`${basePath}/${m.id}`)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: theme => `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: color,
                      width: 42,
                      height: 42,
                      fontSize: 14,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                    src={m.mentee?.profilePicture?.location || undefined}
                  >
                    {initials(m.mentee)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {menteeName}
                      </Typography>
                      <Chip
                        label={isActive ? t('Active') : t('Ended')}
                        color={isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ flexShrink: 0 }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
                      {mentorName}
                    </Typography>
                  </Box>
                </Stack>

                <Divider />
                { totalCount > 0 ? (

                <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, py: 0.75 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {approvedCount} {t('Approved')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {pendingCount} {t('Pending')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {rejectedCount} {t('Rejected')}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {totalCount} {t('Total')}
                  </Typography>
                </Stack>
                ) : (
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, py: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('No Logs Yet')}
                    </Typography>
                  </Stack>
                )}

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}
                >
                  {m.endDate ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('Completed On')} {dayjs(m.endDate).format('MMM D, YYYY')}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('Since')} {dayjs(m.startDate).format('MMM D, YYYY')}
                    </Typography>
                  )}
                  <ChevronRightIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                </Stack>
              </Paper>
            </Grid>
          )
        })}

        {filtered?.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
              <PeopleAltIcon sx={{ fontSize: 44, opacity: 0.25, mb: 1.5, display: 'block', mx: 'auto' }} />
              <Typography variant="body2">
                {search ? t('No mentees match your search') : t('No mentees found')}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
